import IcCheckCircle from 'assets/icons/ic_check_circle.svg'
import { P } from 'components'
import TermsAndConditionsConsent from 'components/TermsAndConditions'
import { recaptchaKey } from 'configs'
import { passwordRequirements } from 'constants'
import { getMetaContent } from 'helpers'
import useRecaptchaV3 from 'hooks/useRecaptchaV3'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { ArrowLeft, Circle, Eye, EyeOff } from 'react-feather'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button, FormGroup, Input, InputGroupText } from 'reactstrap'
import { version } from 'utility/Utils'
import {
  useResetPasswordMutation,
  useUpdatePasswordMutation,
} from '../login-new/loginNewApiSlice'
import './register.css'
import { useRegisterUserMutation } from './registerSlice'

export default function Register() {
  const recaptcha = useRecaptchaV3(recaptchaKey)
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [passwordRequirementsData, setPasswordRequirementsData] = useState(
    passwordRequirements.map((item) => ({ ...item, checked: false })),
  )
  const [isTNCConfirmed, setIsTNCConfirmed] = useState(false)
  const [tncVersion, setTncVersion] = useState(null)
  const location = useLocation()
  const dataState = location.state
  const navigate = useNavigate()
  const [registerUser] = useRegisterUserMutation()
  const [resetPassword] = useResetPasswordMutation()
  const [updatePassword] = useUpdatePasswordMutation()
  const [isTNCOpen, setIsTNCOpen] = useState(false)
  const [titleId, setTitleId] = useState(null)

  const browserHashLocalStorage = localStorage.getItem('browserHash')
  const browserFingerPrintLocalStorage =
    localStorage.getItem('browserFingerPrint')
  const browserCustomFingerPrintLocalStorage = localStorage.getItem(
    'browserCustomFingerPrint',
  )

  const { enqueueSnackbar } = useSnackbar()

  useEffect(() => {
    window.history.pushState(null, '', window.location.href)

    const handleBack = (event) => {
      event.preventDefault()
      window.history.pushState(null, '', window.location.href)
    }

    window.addEventListener('popstate', handleBack)

    return () => {
      window.removeEventListener('popstate', handleBack)
    }
  }, [])

  const handleRegister = async () => {
    try {
      setLoading(true)
      let payload = {
        identity: dataState?.identity,
        deviceHash: dataState?.deviceHash,
        password: password,
        fcmToken: dataState?.fcmToken ?? '',
        packageName: dataState?.packageName || localStorage.getItem('PMB_packageName') || null,
        version: version,
        browserHash: browserHashLocalStorage ?? dataState?.browserHash,
        browserFingerPrint:
          browserFingerPrintLocalStorage ?? dataState?.browserFingerPrint,
        browserCustomFingerPrint:
          browserCustomFingerPrintLocalStorage ??
          dataState?.browserCustomFingerPrint,
        otp: dataState?.otp,
        'g-recaptcha-response': await recaptcha(),
        consentedTypeVersion: Number(tncVersion),
      }
      let res
      if (dataState?.status === 'resetPassword') {
        res = await resetPassword(payload)
      } else if (dataState?.status === 'updatePassword') {
        res = await updatePassword(payload)
      } else {
        res = await registerUser(payload)
      }
      if (res?.error?.data?.meta?.error) {
        throw res
      } else {
        if (dataState?.status === 'resetPassword') {
          enqueueSnackbar('Password berhasil diubah', {
            variant: 'success',
          })
          navigate('/login')
        } else {
          navigate('/questionnaire', {
            state: { ...res?.data?.data },
            replace: true,
          })
        }
      }
    } catch (err) {
      enqueueSnackbar(
        err?.data?.message ||
          err?.data?.error?.message ||
          err?.error?.data?.meta?.message ||
          'Terjadi kesalahan',
        {
          variant: 'error',
        },
      )

      if (!dataState) {
        navigate('/login')
      }
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordChange = (e) => {
    const value = e.target.value
    setPassword(value)

    const updatedRequirements = passwordRequirements.map((req) => {
      let isChecked = false
      switch (req.id) {
        case 1:
          isChecked = /[a-z]/.test(value)
          break
        case 2:
          isChecked = /[A-Z]/.test(value)
          break
        case 3:
          isChecked = value.length >= 8
          break
        case 4:
          isChecked = /[0-9]/.test(value)
          break
        case 5:
          isChecked = /[^a-zA-Z0-9]/.test(value)
          break
        case 6:
          isChecked = value === confirmPassword
          break
        default:
          break
      }
      return { ...req, checked: isChecked }
    })

    setPasswordRequirementsData(updatedRequirements)
  }

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value
    setConfirmPassword(value)

    setPasswordRequirementsData((prev) =>
      prev.map((req) =>
        req.id === 6 ? { ...req, checked: password === value } : req,
      ),
    )
  }

  return (
    <div className="register-container">
      <div className="mobile-header" onClick={() => window.history.back()} data-testid="register-back-button">
        <ArrowLeft size={20} className="chevron-icon" />
      </div>

      <div className="card-wrapper">
        <img
          src={getMetaContent('og:image')}
          alt={`${getMetaContent('og:title')} logo`}
          className="logo"
        />
        <P bold>Buat password Anda</P>

        <div className="mt-4">
          <P size="regular">Password</P>
          <FormGroup className="input-group mt-1">
            <Input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={handlePasswordChange}
              placeholder="Masukkan password"
              className="password-input"
              data-testid="register-password-input"
            />
            <InputGroupText
              className="toggle-password"
              onClick={() => setShowPassword(!showPassword)}
              data-testid="register-show-password-toggle"
            >
              {showPassword ? (
                <EyeOff size={15} style={{ cursor: 'pointer' }} />
              ) : (
                <Eye size={15} style={{ cursor: 'pointer' }} />
              )}
            </InputGroupText>
          </FormGroup>
        </div>
        <div className="mt-2">
          <P size="regular">Konfirmasi password</P>
          <FormGroup className="input-group mt-1">
            <Input
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => handleConfirmPasswordChange(e)}
              placeholder="Masukkan password"
              className="password-input"
              data-testid="register-confirm-password-input"
            />
            <InputGroupText
              className="toggle-password"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              data-testid="register-show-confirm-password-toggle"
            >
              {showConfirmPassword ? (
                <EyeOff size={15} style={{ cursor: 'pointer' }} />
              ) : (
                <Eye size={15} style={{ cursor: 'pointer' }} />
              )}
            </InputGroupText>
          </FormGroup>
        </div>
        <div className="mt-3">
          <P size="regular" bold>
            Password harus terdiri dari setidaknya
          </P>
        </div>
        <div className="mt-2">
          {passwordRequirementsData.map((el) => {
            return (
              <div
                className="d-flex align-items-center gap-2 mb-2"
                key={el.id}
                data-testid={`register-password-requirement-${el.id}`}
              >
                {el.checked ? (
                  <img
                    src={IcCheckCircle}
                    width={20}
                    height={20}
                    alt={`checked-${el.label}`}
                  />
                ) : (
                  <Circle size={20} />
                )}
                <P size="regular" color="#344054">
                  {el.label}
                </P>
              </div>
            )
          })}
        </div>
        <div className="w-100 mt-4">
          <div className="btn-lanjut">
            <div className="mb-2">
              <TermsAndConditionsConsent
                onConfirmed={(verion) => {
                  setTncVersion(verion)
                  setIsTNCConfirmed(true)
                }}
                isOpen={isTNCOpen}
                handleClose={() => setIsTNCOpen(false)}
              />
              <div
                onClick={() => setIsTNCOpen(true)}
                className="mb-1 text-primary text-decoration-underline d-inline-flex align-items-center gap-1"
                style={{ cursor: 'pointer' }}
                data-testid="register-tnc-link"
              >
                <i className="bi bi-file-earmark-text" />
                Baca Syarat dan Ketentuan
              </div>

              {!isTNCConfirmed && (
                <P
                  size="small"
                  color="red"
                  testId="register-tnc-error-message"
                >
                  * Anda harus membaca dan menyetujui Syarat dan Ketentuan
                  sebelum lanjut
                </P>
              )}
            </div>
            <Button
              onClick={() => {
                handleRegister()
              }}
              color="primary"
              className="w-100"
              disabled={
                loading ||
                !passwordRequirementsData.every((req) => req.checked) ||
                !password ||
                !confirmPassword ||
                password !== confirmPassword ||
                !isTNCConfirmed
              }
              data-testid="register-submit-button"
            >
              <P>Lanjut</P>
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
